// src/config/pdfDesign.js

/* Policy/returns section for PDF documents */
export const POLICY_SECTION = `
  <div class="page-break"></div>
  <section class="policy">
    <p>Shipping and handling charges may apply to your order. Dens 'N Dente Healthcare is not responsible for delays in delivery due to causes, directly or indirectly, beyond our reasonable control.</p>
    <p>Orders are normally processed and shipped within twenty-four (24) hours of receipt. For Ontario and Quebec, delivery is typically the next day or within two days of an order being shipped. For other provinces within Canada, delivery can be expected within 3 to 5 business days.</p>
    <p>Prices are subject to change at any time and without notice. Price increases may be passed on in the event of manufacturer or supplier price increases, currency exchange rate fluctuations, and extraordinary circumstances.</p>
    <p><strong>Items must be returned within thirty (30) days of receipt of your order in order to qualify for a credit, exchange or refund.</strong></p>
    <p>Special order items or options, including, but not limited to custom cabinetry, upholstery, and color changes cannot be cancelled. Certain special order items may be cancelled subject to a 20% restocking fee.</p>
    <p>There is a Canada Post disruption impacting delivery of cheques. We have alternative methods to send your remittance. You may set up pre-authorized payments, we accept all major Credit Cards.</p>
    <p>You are responsible for making payments on time. For more information you may email Lydia at <strong>lkrikorian@densndente.ca</strong> or contact your sales rep.</p>
    <p><strong>THE FOLLOWING ITEMS ARE NOT ELIGIBLE FOR A RETURN:</strong></p>
    <ul>
      <li>Opened handpieces and small equipment</li>
      <li>Special order items that are not ordinarily in stock (including custom ordered stock)</li>
      <li>Expired products</li>
      <li>Opened hardware or software</li>
      <li>Personalized items</li>
      <li>Items that cannot be returned to the manufacturer</li>
      <li>Personal protective equipment</li>
    </ul>
  </section>
`;

/**
 * Decodes HTML entities like &amp; and \\u0026
 */
const decodeAmp = (s = "") => s.replace(/\\u0026/g, "&").replace(/&amp;/g, "&");

/**
 * Opens HTML content in a new tab with professional PDF styling
 * Optimized for A4 format with NetSuite invoice compatibility
 *
 * @param {string} rawHtml - The HTML content to display
 * @param {string} title - The document title (default: "Document")
 */
export const openHtmlInNewTab = (rawHtml, title = "Document") => {
  const base = decodeAmp(rawHtml || "");

  // The page opens from a blob: URL, which shares this app's origin — so no
  // script in the document may run (it could reach the customer's session).
  // NetSuite's templates carry none; this makes sure. Placed first in <head>
  // so it applies before anything else is parsed.
  const csp = `<meta http-equiv="Content-Security-Policy" content="script-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none'">`;

  const headBits = `
    <meta charset="utf-8">
    <title>${title}</title>
    <style>
      /* A4 Page setup with proper margins */
      @page { 
        size: A4 portrait; 
        margin: 12mm 8mm 12mm 8mm; 
      }

      /* Base styles - Override NetSuite defaults */
      * { 
        box-sizing: border-box; 
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, Arial, sans-serif !important;
      }
      
      body { 
        margin: 0 !important; 
        padding: 8px !important;
        background: #fff !important; 
        -webkit-print-color-adjust: exact !important; 
        print-color-adjust: exact !important;
        font-size: 10px !important;
        line-height: 1.3 !important;
        color: #333 !important;
        max-width: 194mm !important;
        width: 100% !important;
      }

      /* Container adjustments */
      .content, .policy { 
        max-width: 100% !important;
        width: 100% !important;
        margin: 0 auto !important; 
        padding: 0 !important;
      }

      /* Header table - Company info and invoice title */
      table.header {
        width: 100% !important;
        margin-bottom: 8px !important;
        border: none !important;
      }
      
      table.header td {
        padding: 4px !important;
        font-size: 9px !important;
        vertical-align: top !important;
        border: none !important;
      }
      
      /* Logo styling */
      table.header img {
        max-width: 120px !important;
        max-height: 50px !important;
        margin: 2px 2px 8px 2px !important;
        display: block !important;
        float: none !important;
      }
      
      /* Address text - force to next line after logo */
      table.header span[style*="font-size:11px"] {
        display: block !important;
        clear: both !important;
        margin-top: 5px !important;
        float: none !important;
        line-height: 1.3 !important;
      }
      
      /* Invoice title */
      span.title {
        font-size: 22px !important;
        font-weight: bold !important;
        color: #2c3e50 !important;
      }
      
      span.number {
        font-size: 14px !important;
        font-weight: bold !important;
        color: #e74c3c !important;
      }

      /* Address and billing section */
      td.addressheader {
        font-size: 8px !important;
        font-weight: bold !important;
        padding: 3px 4px 1px 4px !important;
        background-color: #f1f1f1 !important;
        border: 1px solid #ddd !important;
      }
      
      td.address {
        padding: 3px 4px !important;
        font-size: 9px !important;
        line-height: 1.2 !important;
        border: 1px solid #ddd !important;
        vertical-align: top !important;
      }

      /* Total amount box */
      td.totalboxtop {
        font-size: 10px !important;
        background-color: #2c3e50 !important;
        color: white !important;
        font-weight: bold !important;
        text-align: center !important;
        padding: 4px !important;
        border: 1px solid #2c3e50 !important;
      }
      
      td.totalboxmid {
        font-size: 20px !important;
        font-weight: bold !important;
        background-color: #34495e !important;
        color: white !important;
        text-align: center !important;
        padding: 8px 4px !important;
        border: 1px solid #2c3e50 !important;
      }
      
      td.totalboxbot {
        background-color: #ecf0f1 !important;
        font-size: 8px !important;
        padding: 3px 4px !important;
        border: 1px solid #bdc3c7 !important;
        text-align: right !important;
      }

      /* Main tables - Terms, shipping info */
      table.body {
        width: 100% !important;
        margin: 8px 0 !important;
        border-collapse: collapse !important;
      }
      
      table.body th {
        background-color: #34495e !important;
        color: white !important;
        font-size: 8px !important;
        padding: 4px 3px !important;
        text-align: center !important;
        border: 1px solid #2c3e50 !important;
        font-weight: bold !important;
      }
      
      table.body td {
        padding: 3px !important;
        font-size: 9px !important;
        border: 1px solid #ddd !important;
        text-align: center !important;
      }

      /* Item table styling */
      table.itemtable {
        width: 100% !important;
        margin: 8px 0 !important;
        border-collapse: collapse !important;
      }
      
      table.itemtable th {
        background-color: #e3e3e3 !important;
        font-weight: bold !important;
        font-size: 7px !important;
        padding: 4px 2px !important;
        text-align: center !important;
        border: 1px solid #bbb !important;
        line-height: 1.1 !important;
      }
      
      table.itemtable td {
        padding: 3px 2px !important;
        font-size: 8px !important;
        border: 1px solid #ddd !important;
        vertical-align: top !important;
      }
      
      /* Item description styling */
      span.itemname, u strong {
        font-weight: bold !important;
        color: #2c3e50 !important;
        font-size: 9px !important;
      }

      /* Totals table */
      table.total {
        width: 100% !important;
        margin: 8px 0 !important;
        border-collapse: collapse !important;
        page-break-inside: avoid !important;
      }
      
      table.total td {
        padding: 2px 4px !important;
        font-size: 9px !important;
        border: none !important;
      }
      
      tr.totalrow {
        background-color: #e8f4f8 !important;
        font-weight: bold !important;
      }
      
      tr.totalrow td {
        border-top: 2px solid #34495e !important;
        border-bottom: 2px solid #34495e !important;
        font-weight: bold !important;
        padding: 4px !important;
      }

      /* Horizontal rule */
      hr {
        width: 100% !important;
        color: #bdc3c7 !important;
        background-color: #bdc3c7 !important;
        height: 1px !important;
        border: none !important;
        margin: 8px 0 !important;
      }

      /* Payment button styling */
      table[width="80"] {
        margin: 15px auto !important;
        width: auto !important;
      }
      
      table[width="80"] td {
        background-color: #3498db !important;
        border: 2px solid #2980b9 !important;
        padding: 8px 12px !important;
        text-align: center !important;
        border-radius: 4px !important;
      }
      
      table[width="80"] a {
        color: white !important;
        font-size: 10px !important;
        text-decoration: none !important;
        font-weight: bold !important;
        text-transform: uppercase !important;
        display: block !important;
      }

      /* Footer table */
      table.footer {
        width: 100% !important;
        margin-top: 10px !important;
      }
      
      table.footer td {
        padding: 2px !important;
        font-size: 8px !important;
        border: none !important;
      }

      /* Barcode styling (if needed) */
      barcode {
        font-size: 8px !important;
      }

      /* Policy section styling */
      .policy { 
        padding: 10px 0 !important;
        font-size: 9px !important;
        line-height: 1.3 !important;
        page-break-before: always !important;
      }
      
      .policy h2, .policy h3 {
        color: #c0392b !important;
        margin: 8px 0 4px 0 !important;
        font-size: 11px !important;
      }
      
      .policy p { 
        margin: 3px 0 4px 0 !important; 
        text-align: justify !important;
      }
      
      .policy ul { 
        margin: 4px 0 4px 15px !important; 
        padding-left: 0 !important;
      }
      
      .policy li { 
        margin: 1px 0 !important; 
        line-height: 1.2 !important;
      }

      /* Print optimizations */
      @media print {
        body { 
          font-size: 9px !important; 
          padding: 4px !important;
        }
        table.itemtable th { font-size: 6px !important; }
        table.itemtable td { font-size: 7px !important; }
        .policy { font-size: 8px !important; }
        span.title { font-size: 18px !important; }
        td.totalboxmid { font-size: 16px !important; }
      }

      /* Remove XML/DOCTYPE artifacts */
      xml, doctype { display: none !important; }
      
      /* Ensure all text is selectable and properly formatted */
      * {
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        user-select: text !important;
      }
    </style>
  `;

  let fullHtml;
  if (/<!doctype|<html/i.test(base)) {
    fullHtml = (
      /<head[^>]*>/i.test(base)
        ? base.replace(/<head([^>]*)>/i, `<head$1>${csp}`)
        : base.replace(/<html([^>]*)>/i, `<html$1><head>${csp}</head>`)
    )
      .replace(/<\/head>/i, `${headBits}</head>`)
      .replace(/<body([^>]*)>/i, `<body$1><div class="content">`)
      .replace(/<\/body>/i, `</div>${POLICY_SECTION}</body>`);
  } else {
    fullHtml = `<!doctype html>
      <html>
        <head>${csp}${headBits}</head>
        <body>
          <div class="content">${base}</div>
          ${POLICY_SECTION}
        </body>
      </html>`;
  }

  const url = URL.createObjectURL(new Blob([fullHtml], { type: "text/html" }));
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Opens a transaction document fetched from `GET_TRANSACTION_DOCUMENT` in a
 * new tab, according to its type: a PDF as-is; NetSuite's HTML print template
 * (some emailed attachments are one) through `openHtmlInNewTab`, which lays it
 * out as an A4 page and blocks scripts. `title` names the HTML tab.
 *
 * `doc` is `{ contentType, blob }`. The document is fetched through `api`
 * like every other call (CLAUDE.md rule 4 — base URL, retry policy and the
 * concurrency queue all apply), as a blob because it isn't JSON:
 *
 *   const res = await api.get(url, { headers, responseType: "blob" });
 *   await openDocumentResponse(
 *     { contentType: res.headers["content-type"], blob: res.data },
 *     title
 *   );
 */
export const openDocumentResponse = async (
  { contentType, blob },
  title = "Document"
) => {
  const type = contentType || blob?.type || "";
  if (type.startsWith("text/html")) {
    openHtmlInNewTab(await blob.text(), title);
    return;
  }
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  // Give the new tab time to take the blob before revoking it.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

/**
 * Saves a blob fetched through `api` (`responseType: "blob"`) as a file —
 * the invoice PDF from `GET_INVOICE_PDF`, as `<invoice number>.pdf`. An
 * anchor click with `download` keeps the filename (a blob URL opened with
 * `window.open` is saved under a random id) and isn't a popup, so Safari
 * doesn't block it for coming after an `await`.
 */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
